/*
 * The page holds still behind an open overlay on a phone.
 *
 * Every overlay here locks the page with `overflow: hidden` on html or body, and on an iPhone
 * that is not enough: every browser there is WebKit, which keeps the page scrollable behind a
 * modal in some states whatever the overflow says (the keyboard up, the toolbar collapsed). A
 * swipe on a backdrop, on a part of an overlay that does not scroll, or past the end of one that
 * does, moved the page underneath — found on the cart's gift sheet and the menu, 2026-09-29.
 *
 * So while an overlay is open, a vertical swipe goes through only where something between the
 * finger and the overlay can still scroll that way; anywhere else its touchmove is refused.
 * Left alone: sideways swipes (the carousels), pinches, the field being typed in (caret and
 * selection drags), a range slider, and a swipe the browser has already started scrolling (its
 * touchmove is no longer cancelable).
 *
 * The overlays: every open <dialog> — the menu, the pop-up, every sheet and preview — watched
 * through its `open` attribute, so a new one is covered with no code of its own; and the two
 * that are not dialogs, the mobile search and the directory's filter drawer, through `hidden`
 * and the `hidden` class. The listeners are only attached while one is open: a non-passive
 * touchmove on the document makes the browser wait for script before every scroll, a price a
 * page with nothing open should not pay. Each touchmove checks again that something is open,
 * so a dialog removed from the page without closing cannot leave the page unscrollable.
 *
 * No globals.
 */
(function () {
  const OVERLAYS = '[data-mobile-search-modal], [data-directory-drawer]';
  let start = null;
  let active = false;

  function shown(el) {
    return !el.hidden && !el.classList.contains('hidden');
  }

  function anyOpen() {
    if (document.querySelector('dialog[open]')) return true;
    const overlays = document.querySelectorAll(OVERLAYS);
    for (let i = 0; i < overlays.length; i++) {
      if (shown(overlays[i])) return true;
    }
    return false;
  }

  function onStart(event) {
    const touch = event.touches.length === 1 ? event.touches[0] : null;
    start = touch ? { x: touch.clientX, y: touch.clientY } : null;
  }

  function onMove(event) {
    if (!anyOpen()) {
      sync();
      return;
    }
    if (!start || !event.cancelable || event.touches.length !== 1) return;
    const dy = event.touches[0].clientY - start.y;
    const dx = event.touches[0].clientX - start.x;
    if (!dy || Math.abs(dx) > Math.abs(dy)) return;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.matches('input[type="range"]')) return;
    if (
      target === document.activeElement &&
      target.matches('input, textarea, [contenteditable]')
    ) {
      return;
    }
    // Up to and including the open dialog the finger is on (a touch on its backdrop lands on
    // the dialog itself, and some dialogs are their own scroll box), or for an overlay that is
    // not a dialog, up to the page — which itself never counts.
    const layer = target.closest('dialog[open]');
    for (let el = target; el; el = el.parentElement) {
      if (el === document.body || el === document.documentElement) break;
      if (
        el.scrollHeight > el.clientHeight &&
        /(auto|scroll)/.test(window.getComputedStyle(el).overflowY)
      ) {
        if (dy > 0 && el.scrollTop > 0) return;
        if (dy < 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) {
          return;
        }
      }
      if (el === layer) break;
    }
    event.preventDefault();
  }

  function sync() {
    const open = anyOpen();
    if (open === active) return;
    active = open;
    if (open) {
      document.addEventListener('touchstart', onStart, { passive: true });
      document.addEventListener('touchmove', onMove, { passive: false });
    } else {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      start = null;
    }
  }

  function init() {
    if (typeof MutationObserver !== 'function') return;
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, {
      subtree: true,
      attributes: true,
      attributeFilter: ['open'],
    });
    const watchOverlays = function () {
      document.querySelectorAll(OVERLAYS).forEach(function (el) {
        observer.observe(el, {
          attributes: true,
          attributeFilter: ['hidden', 'class'],
        });
      });
    };
    watchOverlays();
    // The theme editor re-renders a section with fresh overlays.
    document.addEventListener('shopify:section:load', function () {
      watchOverlays();
      sync();
    });
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
